import { Icon } from '@acme/assets';
import { FilterDropdown, Input } from '@acme/foundation';
import { useMemo } from 'react';

export type TicketDatePeriod = 'all' | '7d' | '30d';

export interface TicketsFiltersValue {
  search: string;
  statusId?: number;
  categoryId?: number;
  period: TicketDatePeriod;
}

export interface TicketsFiltersApiParams {
  statusId?: number;
  categoryId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export const getTicketsDateRange = (
  period: TicketDatePeriod
): { dateFrom?: string; dateTo?: string } => {
  if (period === 'all') return {};

  const now = new Date();
  const days = period === '7d' ? 7 : 30;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

  return {
    dateFrom: from.toISOString(),
    dateTo: now.toISOString(),
  };
};

export const getTicketsApiParams = (
  value: TicketsFiltersValue
): TicketsFiltersApiParams => {
  const { dateFrom, dateTo } = getTicketsDateRange(value.period);
  return {
    statusId: value.statusId,
    categoryId: value.categoryId,
    dateFrom,
    dateTo,
  };
};

export interface TicketsFiltersProps {
  value: TicketsFiltersValue;
  onChange: (next: TicketsFiltersValue) => void;
  className?: string;
  searchPlaceholder?: string;
}

export const TicketsFilters = (props: TicketsFiltersProps) => {
  const statusOptions = useMemo(
    () => [
      { value: undefined, label: 'Все' },
      { value: 10, label: 'Ожидает ответа' },
      { value: 20, label: 'В обработке' },
      { value: 30, label: 'Закрыто' },
    ],
    []
  );

  const categoryOptions = useMemo(
    () => [
      { value: undefined, label: 'Все' },
      { value: 10, label: 'Заявка' },
      { value: 20, label: 'Договор' },
      { value: 30, label: 'Техническая ошибка' },
      { value: 40, label: 'Документы' },
      { value: 50, label: 'Другое' },
    ],
    []
  );

  const periodOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Все время' },
      { value: '7d' as const, label: '7 дней' },
      { value: '30d' as const, label: '30 дней' },
    ],
    []
  );

  return (
    <div
      className={`flex flex-wrap justify-between items-center gap-3 ${props.className || ''}`}
    >
      <div className="flex items-center gap-3">
        <FilterDropdown
          title="Статус"
          value={props.value.statusId}
          options={statusOptions}
          onChange={(statusId) => props.onChange({ ...props.value, statusId })}
        />

        <FilterDropdown
          title="Категория"
          value={props.value.categoryId}
          options={categoryOptions}
          onChange={(categoryId) =>
            props.onChange({ ...props.value, categoryId })
          }
        />

        <FilterDropdown
          title="Период"
          value={props.value.period}
          options={periodOptions}
          onChange={(period) =>
            props.onChange({
              ...props.value,
              period: (period ?? 'all') as TicketDatePeriod,
            })
          }
        />
      </div>
      <div>
        <Input
          value={props.value.search}
          onChange={(search) => props.onChange({ ...props.value, search })}
          placeholder={props.searchPlaceholder ?? 'Поиск по обращению…'}
          leftIcon={<Icon name="search-outline" size={16} />}
          className="w-full sm:w-80"
        />
      </div>
    </div>
  );
};
